import React from "react";
import { graphql } from "gatsby";
// import Layout from "../components/layout"
// import SEO from "../components/seo"

import { getImage, GatsbyImage } from "gatsby-plugin-image";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { LinkWithUTM as Link } from "../components/LinkWithUTM";
import FacebookIcon from "../components/icons/Facebook";
import LinkedinIcon from "../components/icons/Linkedin";
import MailIcon from "../components/icons/Mail";
import TwitterIcon from "../components/icons/Twitter";
import BlogAuthorCard from "../components/BlogAuthorCard";
import AdviceArticleCarousel from "../components/AdviceArticleCarousel";
import { getArticleSources } from "../utils/articleSources";

import "../../scss/main.scss";
import "../../scss/_blog.scss";

export default function Template(props) {
	const { data } = props;
	const {
		pageContext: { feature_image, publicPath },
	} = props;

	const {
		ghostPost: {
			primary_author,
			primary_tag,
			title,
			meta_title,
			meta_description,
			custom_excerpt,
			excerpt,
			plaintext,
			publishedDisplay,
			updatedDisplay,
			publishedIso,
			updatedIso,
			feature_image_sharp,
			slug,
			html_sections,
		},
		relatedPosts,
	} = data;

	const allNewsblockData = relatedPosts.nodes.filter(
		({ slug: relatedPostSlug }) => relatedPostSlug !== slug
	);
	const canonicalPath = publicPath;
	const shareUrl = `https://www.edyn.care${canonicalPath}`;
	const shareText = `A practical guide from Edyn Care: ${title}`;
	const authorId = `author-${primary_author.slug}`;
	const authorUrl = `/authors/${primary_author.slug}/`;
	const sources = getArticleSources(slug);
	const normalizeDescription = (value = "") =>
		value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
	const rawDescription = normalizeDescription(
		meta_description || custom_excerpt || excerpt || plaintext ||
			`${title}. Practical guidance from Edyn Care for families arranging live-in care at home.`
	);
	const description = rawDescription.length > 155
		? `${rawDescription.slice(0, 152).replace(/\s+\S*$/, "").replace(/[,:;\-]+$/, "")}...`
		: rawDescription;
	const brandSuffix = " | Edyn Care";
	const preferredSeoTitle = (slug === "live-in-care-in-london"
		? "How Live-in Care Works in London"
		: meta_title || title).replace(
		/\s+\|\s+Edyn Care$/i,
		""
	);
	const maxTitleLength = 60 - brandSuffix.length;
	const shortenedSeoTitle =
		preferredSeoTitle.length > maxTitleLength
			? preferredSeoTitle
					.slice(0, maxTitleLength + 1)
					.replace(/\s+\S*$/, "")
					.replace(/[,:;\-]+$/, "")
			: preferredSeoTitle;
	const articleSchema = {
		"@context": "https://schema.org",
		"@type": "Article",
		headline: title,
		description,
		image: feature_image,
		datePublished: publishedIso,
		dateModified: updatedIso || publishedIso,
		author: primary_author && {
			"@type": "Person",
			"@id": `https://www.edyn.care${authorUrl}#person`,
			name: primary_author.name,
			jobTitle: primary_author.bio,
			url: `https://www.edyn.care${authorUrl}`,
		},
		publisher: {
			"@id": "https://www.edyn.care/#organisation",
		},
		mainEntityOfPage: {
			"@type": "WebPage",
			"@id": shareUrl,
		},
		...(sources.length > 0 && {
			citation: sources.map(({ title: sourceTitle, url, publisher }) => ({
				"@type": "CreativeWork",
				name: sourceTitle,
				url,
				publisher: { "@type": "Organization", name: publisher },
			})),
		}),
	};

	return (
		<>
			<Seo
				title={`${shortenedSeoTitle}${brandSuffix}`}
				description={description}
				path={canonicalPath}
				image={feature_image}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{ name: "Advice", path: "/blog/" },
					{ name: title, path: canonicalPath },
				]}
			>
				<script type="application/ld+json">
					{JSON.stringify(articleSchema)}
				</script>
			</Seo>
			<div className="container edyn-commercial-shell edyn-article-shell">
				<Header />
				<main id="main-content" className="blog-post blog-post--article">
					<div className="blog-post__above-the-fold blog-post__above-the-fold--article">
						<div className="blog-post__article-masthead">
							<Link className="blog-post__back" to="/blog/">
								Back to advice <span aria-hidden="true">&#8592;</span>
							</Link>
							<p className="eyebrow eyebrow--light">
								{primary_tag?.name || "Edyn advice"}
							</p>
							<h1 className="blog-post__above-the-fold-title">{title}</h1>
							<div className="blog-post__article-meta">
								<span>
									Published <time dateTime={publishedIso}>{publishedDisplay}</time>
								</span>
								{updatedDisplay !== publishedDisplay && (
									<span>
										Updated <time dateTime={updatedIso}>{updatedDisplay}</time>
									</span>
								)}
								<span>Practical guidance from Edyn</span>
							</div>
						</div>
					</div>
					<GatsbyImage
						className="blog-post__hero-image"
						image={getImage(feature_image_sharp)}
						alt="Blog post image"
						objectFit="cover"
					/>
					<section className="blog-post-section">
						<div className="blog-post__content">
							{html_sections.json
								.filter(({ tagName }) => tagName !== null)
								.map(({ tagName, innerHtml, attributes }) => {
									if (
										attributes &&
										attributes.data_edyn_component === "primary-button"
									) {
										if (attributes.data_edyn_url.startsWith("tel:"))
											return (
												<div className="blog-button-wrapper">
													<a
														href={attributes.data_edyn_url}
														className="btn-primary"
													>
														{innerHtml}
													</a>
												</div>
											);
										return (
											<div className="blog-button-wrapper">
												<Link
													to={attributes.data_edyn_url}
													className="btn-primary"
												>
													{innerHtml}
												</Link>
											</div>
										);
									}
									const HtmlTag = tagName;
									if (tagName.toLowerCase() === "hr") return <hr />;
									return (
										<HtmlTag
											id={attributes.id}
											className={attributes.class}
											dangerouslySetInnerHTML={{ __html: innerHtml }}
										/>
									);
								})}
						</div>

						{sources.length > 0 && (
							<aside className="blog-post__sources" aria-labelledby="trusted-sources-title">
								<p className="eyebrow">Trusted sources</p>
								<h2 id="trusted-sources-title">Guidance used for this article</h2>
								<ul>
									{sources.map(({ title: sourceTitle, url, publisher }) => (
										<li key={url}>
											<a href={url} target="_blank" rel="noreferrer">
												{sourceTitle} <span>({publisher})</span>
											</a>
										</li>
									))}
								</ul>
							</aside>
						)}

						<div className="blog-post__socials" aria-label="Share this guide">
							<p>Share this guide</p>
							<a
								href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
									shareUrl
								)}`}
								target="_blank"
								rel="noreferrer"
								aria-label="Share on Facebook"
							>
								<FacebookIcon className="social-icon" />
							</a>
							<a
								href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
									shareText
								)}&url=${encodeURIComponent(shareUrl)}`}
								target="_blank"
								rel="noreferrer"
								aria-label="Share on X"
							>
								<TwitterIcon className="social-icon" />
							</a>
							<a
								href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
									shareUrl
								)}`}
								target="_blank"
								rel="noreferrer"
								aria-label="Share on LinkedIn"
							>
								<LinkedinIcon className="social-icon" />
							</a>
							<a
								href={`mailto:?subject=${encodeURIComponent(
									title
								)}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
								aria-label="Share by email"
							>
								<MailIcon className="social-icon" />
							</a>
						</div>

						<BlogAuthorCard
							authorId={authorId}
							authorTitle={primary_author.bio}
							authorName={primary_author.name}
							authorProfileImage={getImage(primary_author.profile_image_sharp)}
							authorUrl={authorUrl}
						/>
						<p className="blog-post__editorial-note">
							We review our advice when guidance or care standards change. Read
							 our <Link to="/editorial-policy/">editorial standards</Link> and
							 speak to a qualified professional for medical, legal or financial
							 decisions.
						</p>
					</section>
					{allNewsblockData && allNewsblockData.length > 0 && (
						<section className="article-related" aria-labelledby="related-posts-title">
							<div className="article-related__inner">
								<div className="article-related__heading">
									<p className="eyebrow">Keep reading</p>
									<h2 id="related-posts-title">Related posts</h2>
								</div>
								<AdviceArticleCarousel posts={allNewsblockData} />
							</div>
						</section>
					)}
				</main>
				<Footer />
			</div>
		</>
	);
}

export const pageQuery = graphql`
	query($slug: String!, $tagSlug: String!) {
		ghostPost(slug: { eq: $slug }) {
			html
			html_sections {
				json {
					tagName
					innerHtml
					attributes {
						data_edyn_component
						data_edyn_url
						class
						id
					}
				}
			}
			title
			meta_title
			meta_description
			custom_excerpt
			excerpt
			plaintext
			primary_tag {
				name
			}
			slug
			publishedDisplay: published_at(formatString: "DD MMM YYYY")
			updatedDisplay: updated_at(formatString: "DD MMM YYYY")
			publishedIso: published_at
			updatedIso: updated_at
			feature_image_sharp {
				childImageSharp {
					gatsbyImageData
				}
			}
			primary_author {
				bio
				name
				slug
				profile_image_sharp {
					childImageSharp {
						gatsbyImageData
					}
				}
			}
		}
		relatedPosts: allGhostPost(
			filter: { primary_tag: { slug: { eq: $tagSlug } } }
		) {
			nodes {
				title
				slug
				feature_image_sharp {
					childImageSharp {
						gatsbyImageData
					}
				}
				excerpt
				published_at(formatString: "DD MMM YYYY")
			}
		}
	}
`;
